<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useArdhisasaTicketsStore } from 'dashboard/stores/ardhisasaTickets';

const store = useArdhisasaTicketsStore();
const now = ref(Date.now());

const ALMOST_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

let timer = null;

onMounted(() => {
  store.initRefreshTimer();
  timer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  clearInterval(timer);
});

const remaining = computed(() => {
  if (!store.token || !store.tokenExpiry) return null;
  return Number(store.tokenExpiry) - now.value;
});

const tokenStatus = computed(() => {
  if (!store.token) return 'none';
  if (!store.tokenExpiry) return 'valid';
  if (remaining.value <= 0) return 'expired';
  if (remaining.value < ALMOST_EXPIRY_MS) return 'expiring';
  return 'valid';
});

const colorClass = computed(
  () =>
    ({
      none: 'bg-n-slate-12',
      expired: 'bg-n-ruby-9',
      expiring: 'bg-amber-400',
      valid: 'bg-green-500',
    })[tokenStatus.value]
);

const countdown = computed(() => {
  if (!remaining.value || remaining.value <= 0) return null;
  const totalSeconds = Math.floor(remaining.value / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
});

const tooltipLabel = computed(() => {
  if (tokenStatus.value === 'none') return 'Ardhisasa Logged Out';
  if (tokenStatus.value === 'expired') return 'Click to Login to Ardhisasa';
  if (tokenStatus.value === 'expiring')
    return countdown.value
      ? `Refresh Ardhisasa Token · ${countdown.value}`
      : 'Refresh Ardhisasa Token';
  return countdown.value
    ? `Ardhisasa Logged In · ${countdown.value}`
    : 'Ardhisasa Logged In';
});

function onClick(e) {
  e.stopPropagation();
}
</script>

<template>
  <button
    v-tooltip="tooltipLabel"
    class="flex items-center gap-1 flex-shrink-0 focus:outline-none cursor-pointer"
    @click="onClick"
  >
    <span class="size-2 rounded-full" :class="colorClass" />
    <span
      v-if="countdown && tokenStatus !== 'valid'"
      class="text-[10px] leading-none font-medium tabular-nums"
      :class="tokenStatus === 'expiring' ? 'text-amber-500' : 'text-n-ruby-9'"
    >
      {{ countdown }}
    </span>
  </button>
</template>
